import { html, LitElement, nothing, svg } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

type DepreciationType = 'linear' | 'digressive';

interface YearMonth {
    year: number;
    month: number;
}

interface LifeChangeInput {
    when: string; // 'YYYY-MM'
    initial: number;
}

interface AssetPlanPosition {
    when: YearMonth;
    calculatedDepreciation: number;
    sum: number;
    sumThisYear: number;
}

interface Dictionary {
    depreciationCalculator: string;
    inputLabel: string;
    resultLabel: string;
    fixedAsset: string;
    initialValue: string;
    depreciationRate: string;
    startOfDepreciation: string;
    depreciationMethod: string;
    linear: string;
    digressive: string;
    degressionCoefficients: string;
    addChangeValue: string;
    recalculate: string;
    valueChanges: string;
    lp: string;
    month: string;
    change: string;
    remove: string;
    depreciationPlan: string;
    year: string;
    amountWriteOf: string;
    totalWriteOf: string;
    totalInTheYear: string;
    loading: string;
    error: string;
    summaryInitial: string;
    summaryDuration: string;
    summaryMonths: string;
    monthNames: string[];
    numberLocale: string;
    summaryTotal: string;
    summaryEnd: string;
}

const STORAGE_KEY = 'arti24-depreciation-calculator:state';

function todayYearMonth(): string {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function parseYearMonth(value: string): YearMonth {
    const [year, month] = value.split('-').map(Number);
    return { year, month };
}

const ICONS = {
    money: svg`<path d="M2 6h20v12H2z" /><circle cx="12" cy="12" r="3" /><path d="M6 6v.01M18 6v.01M6 18v.01M18 18v.01" />`,
    percent: svg`<circle cx="7" cy="7" r="2.5" /><circle cx="17" cy="17" r="2.5" /><path d="M18 6 6 18" />`,
    calendar: svg`<rect x="3" y="5" width="18" height="16" rx="2" /><path d="M8 3v4M16 3v4M3 10h18" />`,
    chart: svg`<path d="M3 3v16a2 2 0 0 0 2 2h16" /><path d="m19 9-5 5-4-4-3 3" />`,
    plus: svg`<path d="M12 5v14M5 12h14" />`,
    refresh: svg`<path d="M21 12a9 9 0 1 1-2.64-6.36" /><path d="M21 3v6h-6" />`,
    trash: svg`<path d="M3 6h18" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6" />`,
    spinner: svg`<path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />`,
};

function fieldIcon(name: keyof typeof ICONS) {
    return svg`
        <svg class="arti24-dc__input-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">${ICONS[name]}</svg>
    `;
}

function headerIcon(name: keyof typeof ICONS) {
    return svg` <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">${ICONS[name]}</svg> `;
}

/**
 * Samodzielny kalkulator amortyzacyjny (light DOM, zgodnie z pozostałymi
 * komponentami projektu). Odtwarza logikę i kontrakt API dawnego frontendu
 * Angular (arti-angular-app/src/app/asset-calculator) - to samo pole
 * dla wartości/metody amortyzacji, ten sam JSON wysyłany do backendu
 * (assets-services, POST /rest-api/assets/calculate).
 */
@customElement('arti24-depreciation-calculator')
export class Arti24DepreciationCalculator extends LitElement {
    protected createRenderRoot(): HTMLElement {
        return this;
    }

    @property({ type: String, attribute: 'api-url' })
    apiUrl = 'https://api.arti24.eu/rest-api/assets/calculate';

    @property({ type: Object, attribute: 'ld' })
    ld?: Dictionary;

    /**
     * Ukrywa wewnętrzny <h2> - przydatne, gdy strona hosta ma już
     * własny nagłówek (np. sekcję hero) i powielanie tytułu byłoby zbędne.
     */
    @property({ type: Boolean, attribute: 'hide-title' })
    hideTitle = false;

    @state() private initialValue = 6000;
    @state() private rate = 20;
    @state() private startDepreciation = todayYearMonth();
    @state() private depreciationType: DepreciationType = 'linear';
    @state() private factor = 2;
    @state() private lifeChanges: LifeChangeInput[] = [];

    @state() private results: AssetPlanPosition[] = [];
    @state() private loading = false;
    @state() private errorMessage = '';

    connectedCallback(): void {
        super.connectedCallback();
        this.restoreState();
    }

    private get dict(): Dictionary {
        if (!this.ld) throw new Error('Depreciation calculator requires LD');
        return this.ld;
    }

    private formatNumber(value: number): string {
        return value.toLocaleString(this.dict.numberLocale, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    }

    private formatMonthYear(when: YearMonth): string {
        return `${this.dict.monthNames[when.month - 1]} ${when.year}`;
    }

    private restoreState() {
        try {
            // Wewnątrz try: przy zablokowanej pamięci przeglądarki już sam
            // dostęp do localStorage rzuca wyjątek.
            const raw = localStorage.getItem(STORAGE_KEY);
            if (!raw) return;

            const saved = JSON.parse(raw);
            this.initialValue = saved.initialValue ?? this.initialValue;
            this.rate = saved.rate ?? this.rate;
            this.startDepreciation = saved.startDepreciation ?? this.startDepreciation;
            this.depreciationType = saved.depreciationType ?? this.depreciationType;
            this.factor = saved.factor ?? this.factor;
            this.lifeChanges = Array.isArray(saved.lifeChanges) ? saved.lifeChanges : [];
            this.recalculate();
        } catch {
            // Ignorujemy uszkodzony lub niedostępny zapis - zostają wartości domyślne.
        }
    }

    private saveState() {
        const toSave = {
            initialValue: this.initialValue,
            rate: this.rate,
            startDepreciation: this.startDepreciation,
            depreciationType: this.depreciationType,
            factor: this.factor,
            lifeChanges: this.lifeChanges,
        };
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
        } catch {
            // Zapamiętanie formularza jest tylko udogodnieniem - pełna albo
            // zablokowana pamięć przeglądarki nie może blokować obliczeń.
        }
    }

    private buildRequestBody() {
        const start = parseYearMonth(this.startDepreciation);

        const life = [
            { when: start, initial: this.initialValue, residual: 0, depreciation: 0 },
            ...this.lifeChanges.map((change) => ({
                when: parseYearMonth(change.when),
                initial: change.initial,
                residual: 0,
                depreciation: 0,
            })),
        ];

        const depreciationMethods = [
            {
                year: start.year,
                type: this.depreciationType,
                rate: this.rate,
                factor: this.factor,
            },
        ];

        return { start, life, depreciationMethods };
    }

    private async recalculate() {
        this.loading = true;
        this.errorMessage = '';
        this.saveState();

        try {
            const response = await fetch(this.apiUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(this.buildRequestBody()),
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const positions: AssetPlanPosition[] = await response.json();

            let sum = 0;
            let sumThisYear = 0;

            positions.forEach((pos) => {
                pos.calculatedDepreciation *= 0.01;
                sum += pos.calculatedDepreciation;
                pos.sum = sum;
                sumThisYear =
                    pos.when.month === 1 ? pos.calculatedDepreciation : sumThisYear + pos.calculatedDepreciation;
                pos.sumThisYear = sumThisYear;
            });

            this.results = positions;
        } catch {
            this.errorMessage = this.dict.error;
            this.results = [];
        } finally {
            this.loading = false;
        }
    }

    private addLifeChange() {
        this.lifeChanges = [...this.lifeChanges, { when: this.startDepreciation, initial: 1000 }];
    }

    private removeLifeChange(index: number) {
        this.lifeChanges = this.lifeChanges.filter((_, i) => i !== index);
    }

    private updateLifeChange(index: number, patch: Partial<LifeChangeInput>) {
        this.lifeChanges = this.lifeChanges.map((change, i) => (i === index ? { ...change, ...patch } : change));
    }

    render() {
        if (!this.ld) return nothing;
        const d = this.dict;
        const totalMonths = this.results.length;
        const totalSum = totalMonths ? this.results[totalMonths - 1].sum : 0;
        const endWhen = totalMonths ? this.results[totalMonths - 1].when : null;

        return html`
            <style>
                arti24-depreciation-calculator {
                    display: block;
                }

                arti24-depreciation-calculator .arti24-depreciation-calculator {
                    display: flex;
                    flex-direction: column;
                    gap: var(--arti24-space-8, 2rem);
                    min-width: 0;
                    max-width: 100%;

                    font-family: inherit;
                    color: var(--arti24-color-text, #1f2937);
                }

                arti24-depreciation-calculator h2 {
                    font-size: var(--arti24-text-2xl, 1.5rem);
                    font-weight: 800;
                    margin: 0 0 var(--arti24-space-4, 1rem) 0;
                    color: var(--arti24-color-text, #1f2937);
                }

                arti24-depreciation-calculator .arti24-dc__card-header {
                    display: flex;
                    align-items: center;
                    gap: 0.65rem;
                    margin: 0 0 var(--arti24-space-5, 1.25rem) 0;
                    padding-bottom: var(--arti24-space-4, 1rem);
                    border-bottom: 1px solid var(--arti24-color-border, #e5e7eb);
                }

                arti24-depreciation-calculator .arti24-dc__card-heading {
                    display: flex;
                    flex-direction: column;
                    gap: 0.15rem;
                }

                arti24-depreciation-calculator .arti24-dc__section-label {
                    color: var(--arti24-color-primary, #2563eb);
                    font-size: 0.68rem;
                    font-weight: 800;
                    letter-spacing: 0.09em;
                    line-height: 1;
                    text-transform: uppercase;
                }

                arti24-depreciation-calculator .arti24-dc__card-icon {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;

                    width: 2.1rem;
                    height: 2.1rem;

                    border-radius: var(--arti24-radius-md, 8px);
                    background: color-mix(in srgb, var(--arti24-color-primary, #2563eb) 12%, transparent);
                    color: var(--arti24-color-primary, #2563eb);
                }

                arti24-depreciation-calculator .arti24-dc__card-icon svg {
                    width: 1.15rem;
                    height: 1.15rem;
                    fill: none;
                    stroke: currentColor;
                    stroke-width: 2;
                    stroke-linecap: round;
                    stroke-linejoin: round;
                }

                arti24-depreciation-calculator h3 {
                    font-size: var(--arti24-text-lg, 1.125rem);
                    font-weight: 700;
                    margin: 0;
                }

                arti24-depreciation-calculator .arti24-dc__top {
                    display: grid;
                    grid-template-columns: 1fr;
                    gap: var(--arti24-space-6, 1.5rem);
                    align-items: start;
                }

                arti24-depreciation-calculator .arti24-dc__fields {
                    display: grid;
                    grid-template-columns: minmax(190px, 1.2fr) minmax(150px, 0.75fr) minmax(190px, 1fr) minmax(180px, 1fr);
                    gap: var(--arti24-space-4, 1rem);
                    align-items: end;
                }

                arti24-depreciation-calculator .arti24-dc__card {
                    min-width: 0;
                    max-width: 100%;
                    box-sizing: border-box;
                    background: var(--arti24-color-surface, #fff);
                    border: 1px solid var(--arti24-color-border, #e5e7eb);
                    border-radius: var(--arti24-radius-lg, 14px);
                    box-shadow: var(--arti24-shadow-card, 0 1px 3px rgba(15, 23, 42, 0.08));
                    padding: var(--arti24-space-6, 1.5rem);
                }

                arti24-depreciation-calculator .arti24-dc__card--input {
                    border-top: 3px solid var(--arti24-color-primary, #2563eb);
                    background: linear-gradient(
                        180deg,
                        color-mix(in srgb, var(--arti24-color-primary, #2563eb) 3%, var(--arti24-color-surface, #fff)),
                        var(--arti24-color-surface, #fff) 45%
                    );
                }

                arti24-depreciation-calculator .arti24-dc__card--result {
                    border-top: 3px solid var(--arti24-color-accent-green, #10b981);
                    box-shadow: 0 12px 32px color-mix(in srgb, var(--arti24-color-primary, #2563eb) 9%, transparent);
                }

                arti24-depreciation-calculator .arti24-dc__card--result .arti24-dc__section-label {
                    color: var(--arti24-color-accent-green, #059669);
                }

                arti24-depreciation-calculator .arti24-dc__field {
                    display: flex;
                    flex-direction: column;
                    gap: 0.4rem;
                    margin: 0;
                }

                @media (max-width: 1050px) {
                    arti24-depreciation-calculator .arti24-dc__fields {
                        grid-template-columns: repeat(2, minmax(0, 1fr));
                    }
                }

                @media (max-width: 600px) {
                    arti24-depreciation-calculator .arti24-dc__fields {
                        grid-template-columns: 1fr;
                    }
                }

                arti24-depreciation-calculator .arti24-dc__field label {
                    font-size: 0.78rem;
                    font-weight: 600;
                    color: var(--arti24-color-text-muted, #6b7280);
                    text-transform: uppercase;
                    letter-spacing: 0.02em;
                }

                arti24-depreciation-calculator .arti24-dc__input-wrap {
                    position: relative;
                    display: flex;
                    align-items: center;
                }

                arti24-depreciation-calculator .arti24-dc__input-icon {
                    position: absolute;
                    left: 0.75rem;
                    width: 1.05rem;
                    height: 1.05rem;
                    flex-shrink: 0;
                    color: var(--arti24-color-text-muted, #9ca3af);
                    fill: none;
                    stroke: currentColor;
                    stroke-width: 2;
                    stroke-linecap: round;
                    stroke-linejoin: round;
                    pointer-events: none;
                }

                arti24-depreciation-calculator .arti24-dc__input-suffix {
                    position: absolute;
                    right: 0.8rem;
                    font-size: 0.8rem;
                    font-weight: 600;
                    color: var(--arti24-color-text-muted, #9ca3af);
                    pointer-events: none;
                }

                arti24-depreciation-calculator .arti24-dc__input-wrap select ~ .arti24-dc__input-suffix {
                    display: flex;
                    color: var(--arti24-color-text-muted, #9ca3af);
                }

                arti24-depreciation-calculator .arti24-dc__field input,
                arti24-depreciation-calculator .arti24-dc__field select {
                    width: 100%;
                    box-sizing: border-box;
                    padding: 0.65rem 2.4rem 0.65rem 2.4rem;
                    border-radius: var(--arti24-radius-md, 10px);
                    border: 1px solid var(--arti24-color-border, #d1d5db);
                    background: var(--arti24-color-surface-subtle, #f8fafc);
                    color: var(--arti24-color-text, #1f2937);
                    font: inherit;
                    font-size: 0.92rem;
                    font-weight: 600;
                    min-height: 3rem;
                    appearance: none;
                    -webkit-appearance: none;
                    -moz-appearance: none;
                    transition:
                        border-color 140ms ease,
                        box-shadow 140ms ease;
                }

                arti24-depreciation-calculator .arti24-dc__field select {
                    cursor: pointer;
                    padding-right: 2.4rem;
                }

                arti24-depreciation-calculator .arti24-dc__field input::-webkit-inner-spin-button,
                arti24-depreciation-calculator .arti24-dc__field input::-webkit-outer-spin-button {
                    -webkit-appearance: none;
                    margin: 0;
                }

                arti24-depreciation-calculator .arti24-dc__field input[type='number'] {
                    -moz-appearance: textfield;
                }

                arti24-depreciation-calculator .arti24-dc__field input:focus,
                arti24-depreciation-calculator .arti24-dc__field select:focus {
                    outline: none;
                    border-color: var(--arti24-color-primary, #2563eb);
                    background: var(--arti24-color-surface, #fff);
                    box-shadow: 0 0 0 3px color-mix(in srgb, var(--arti24-color-primary, #2563eb) 18%, transparent);
                }

                arti24-depreciation-calculator .arti24-dc__chevron {
                    position: absolute;
                    right: 0.8rem;
                    width: 0.9rem;
                    height: 0.9rem;
                    color: var(--arti24-color-text-muted, #9ca3af);
                    fill: none;
                    stroke: currentColor;
                    stroke-width: 2;
                    stroke-linecap: round;
                    stroke-linejoin: round;
                    pointer-events: none;
                }

                arti24-depreciation-calculator .arti24-dc__actions {
                    display: flex;
                    flex-wrap: wrap;
                    align-items: center;
                    gap: var(--arti24-space-3, 0.75rem);
                    margin-top: var(--arti24-space-5, 1.25rem);
                    padding-top: var(--arti24-space-5, 1.25rem);
                    border-top: 1px solid var(--arti24-color-border, #e5e7eb);
                }

                arti24-depreciation-calculator button {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 0.45rem;

                    font: inherit;
                    font-size: 0.88rem;
                    font-weight: 700;
                    padding: 0.6rem 1.15rem;
                    border-radius: var(--arti24-radius-md, 10px);
                    border: 1px solid var(--arti24-color-border, #d1d5db);
                    background: var(--arti24-color-surface-subtle, #f3f4f6);
                    color: var(--arti24-color-text, #1f2937);
                    cursor: pointer;
                    transition:
                        background 160ms ease,
                        border-color 160ms ease,
                        transform 160ms ease,
                        box-shadow 160ms ease;
                }

                arti24-depreciation-calculator button svg {
                    width: 1rem;
                    height: 1rem;
                    flex-shrink: 0;
                    fill: none;
                    stroke: currentColor;
                    stroke-width: 2;
                    stroke-linecap: round;
                    stroke-linejoin: round;
                }

                arti24-depreciation-calculator button:hover {
                    background: var(--arti24-color-surface-hover, #e5e7eb);
                }

                arti24-depreciation-calculator button.arti24-dc__primary {
                    margin-left: auto;
                    min-width: 8.5rem;
                    background: var(--arti24-color-primary, #2563eb);
                    border-color: var(--arti24-color-primary, #2563eb);
                    color: var(--arti24-color-on-primary, #fff);
                    box-shadow: 0 4px 12px color-mix(in srgb, var(--arti24-color-primary, #2563eb) 30%, transparent);
                }

                arti24-depreciation-calculator button.arti24-dc__primary:hover {
                    transform: translateY(-1px);
                    box-shadow: 0 6px 16px color-mix(in srgb, var(--arti24-color-primary, #2563eb) 38%, transparent);
                }

                arti24-depreciation-calculator button.arti24-dc__primary svg {
                    transition: transform 400ms ease;
                }

                arti24-depreciation-calculator button.arti24-dc__primary[disabled] svg {
                    animation: arti24-dc-spin 900ms linear infinite;
                }

                @keyframes arti24-dc-spin {
                    to {
                        transform: rotate(360deg);
                    }
                }

                arti24-depreciation-calculator button:disabled {
                    opacity: 0.7;
                    cursor: default;
                    transform: none;
                }

                arti24-depreciation-calculator .arti24-dc__summary {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
                    gap: var(--arti24-space-3, 0.75rem);
                    margin: 0 0 var(--arti24-space-5, 1.25rem) 0;
                }

                arti24-depreciation-calculator .arti24-dc__stat {
                    display: flex;
                    flex-direction: column;
                    gap: 0.3rem;
                    padding: 0.9rem 1rem;
                    background: var(--arti24-color-surface-subtle, #f9fafb);
                    border: 1px solid var(--arti24-color-border, #e5e7eb);
                    border-radius: var(--arti24-radius-md, 10px);
                }

                arti24-depreciation-calculator .arti24-dc__stat-label {
                    font-size: 0.72rem;
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.03em;
                    color: var(--arti24-color-text-muted, #6b7280);
                }

                arti24-depreciation-calculator .arti24-dc__stat-value {
                    font-size: 1.15rem;
                    font-weight: 800;
                    color: var(--arti24-color-text, #1f2937);
                    line-height: 1.2;
                }

                arti24-depreciation-calculator .arti24-dc__stat--accent .arti24-dc__stat-value {
                    color: var(--arti24-color-primary, #2563eb);
                }

                arti24-depreciation-calculator .arti24-dc__stat--accent {
                    background: color-mix(in srgb, var(--arti24-color-primary, #2563eb) 8%, var(--arti24-color-surface, #fff));
                    border-color: color-mix(in srgb, var(--arti24-color-primary, #2563eb) 28%, var(--arti24-color-border, #e5e7eb));
                }

                @media (max-width: 600px) {
                    arti24-depreciation-calculator .arti24-dc__actions button {
                        width: 100%;
                    }

                    arti24-depreciation-calculator button.arti24-dc__primary {
                        margin-left: 0;
                    }
                }

                arti24-depreciation-calculator table {
                    width: 100%;
                    border-collapse: collapse;
                    font-size: var(--arti24-text-sm, 0.875rem);
                }

                arti24-depreciation-calculator th,
                arti24-depreciation-calculator td {
                    padding: 0.55rem 0.65rem;
                    border-bottom: 1px solid var(--arti24-color-border, #e5e7eb);
                    text-align: right;
                    font-variant-numeric: tabular-nums;
                }

                arti24-depreciation-calculator th:first-child,
                arti24-depreciation-calculator td:first-child {
                    text-align: left;
                    color: var(--arti24-color-text-muted, #9ca3af);
                    font-weight: 500;
                }

                arti24-depreciation-calculator thead th {
                    font-weight: 700;
                    color: var(--arti24-color-text-muted, #6b7280);
                    text-transform: uppercase;
                    font-size: 0.7rem;
                    letter-spacing: 0.04em;
                    border-bottom-width: 2px;
                }

                arti24-depreciation-calculator tbody tr {
                    transition: background 120ms ease;
                }

                arti24-depreciation-calculator .arti24-dc__month-row--odd {
                    background: color-mix(
                        in srgb,
                        var(--arti24-color-primary, #2563eb) 4%,
                        var(--arti24-color-surface, #fff)
                    );
                }

                arti24-depreciation-calculator .arti24-dc__month-row--even {
                    background: color-mix(
                        in srgb,
                        var(--arti24-color-primary, #2563eb) 10%,
                        var(--arti24-color-surface, #fff)
                    );
                }

                arti24-depreciation-calculator tbody tr:hover {
                    background: color-mix(in srgb, var(--arti24-color-primary, #2563eb) 6%, transparent);
                }

                arti24-depreciation-calculator .arti24-dc__year-total td {
                    text-align: right;
                    font-weight: 700;
                    color: var(--arti24-color-primary, #2563eb);
                    background: color-mix(in srgb, var(--arti24-color-primary, #2563eb) 5%, transparent);
                    border-bottom: 1px solid var(--arti24-color-border, #e5e7eb);
                    padding-top: 0.5rem;
                    padding-bottom: 0.5rem;
                }

                arti24-depreciation-calculator .arti24-dc__table-wrap {
                    width: 100%;
                    max-width: 100%;
                    overflow-x: auto;
                    overscroll-behavior-inline: contain;
                    -webkit-overflow-scrolling: touch;
                }

                @media (max-width: 600px) {
                    arti24-depreciation-calculator .arti24-dc__card {
                        padding: var(--arti24-space-4, 1rem);
                    }

                    arti24-depreciation-calculator .arti24-dc__card--result table {
                        display: block;
                        font-size: 0.78rem;
                    }

                    arti24-depreciation-calculator .arti24-dc__card--result thead,
                    arti24-depreciation-calculator .arti24-dc__card--result tbody {
                        display: block;
                        width: 100%;
                    }

                    arti24-depreciation-calculator .arti24-dc__card--result tr {
                        display: grid;
                        grid-template-columns:
                            minmax(1.5rem, 0.35fr)
                            minmax(2.8rem, 0.75fr)
                            minmax(2.4rem, 0.6fr)
                            minmax(3.7rem, 1fr)
                            minmax(4.4rem, 1.25fr);
                        width: 100%;
                    }

                    arti24-depreciation-calculator .arti24-dc__card--result th,
                    arti24-depreciation-calculator .arti24-dc__card--result td {
                        min-width: 0;
                        padding: 0.5rem 0.25rem;
                        overflow-wrap: anywhere;
                    }

                    arti24-depreciation-calculator .arti24-dc__card--result thead th {
                        font-size: 0.62rem;
                        letter-spacing: 0.01em;
                    }

                    arti24-depreciation-calculator .arti24-dc__card--result .arti24-dc__year-total {
                        display: block;
                    }

                    arti24-depreciation-calculator .arti24-dc__card--result .arti24-dc__year-total td {
                        display: block;
                        width: 100%;
                        box-sizing: border-box;
                        white-space: normal;
                    }
                }

                arti24-depreciation-calculator td input {
                    width: 100%;
                    box-sizing: border-box;
                    padding: 0.4rem 0.55rem;
                    border-radius: var(--arti24-radius-sm, 6px);
                    border: 1px solid var(--arti24-color-border, #d1d5db);
                    background: var(--arti24-color-surface, #fff);
                    color: var(--arti24-color-text, #1f2937);
                    font: inherit;
                    font-size: 0.85rem;
                    font-weight: 600;
                    text-align: right;
                }

                arti24-depreciation-calculator td input:focus {
                    outline: none;
                    border-color: var(--arti24-color-primary, #2563eb);
                    box-shadow: 0 0 0 3px color-mix(in srgb, var(--arti24-color-primary, #2563eb) 18%, transparent);
                }

                arti24-depreciation-calculator .arti24-dc__remove {
                    padding: 0.35rem 0.5rem;
                    font-size: 0.78rem;
                    color: var(--arti24-color-text-muted, #6b7280);
                    background: transparent;
                    border-color: transparent;
                }

                arti24-depreciation-calculator .arti24-dc__remove:hover {
                    color: var(--arti24-color-danger, #dc2626);
                    background: color-mix(in srgb, var(--arti24-color-danger, #dc2626) 10%, transparent);
                    border-color: transparent;
                }

                arti24-depreciation-calculator .arti24-dc__status {
                    display: flex;
                    align-items: center;
                    gap: 0.4rem;
                    font-size: var(--arti24-text-sm, 0.875rem);
                    color: var(--arti24-color-text-muted, #6b7280);
                    margin-top: var(--arti24-space-3, 0.75rem);
                }

                arti24-depreciation-calculator .arti24-dc__status svg {
                    width: 0.95rem;
                    height: 0.95rem;
                    fill: none;
                    stroke: currentColor;
                    stroke-width: 2;
                    stroke-linecap: round;
                    animation: arti24-dc-spin 900ms linear infinite;
                }

                arti24-depreciation-calculator .arti24-dc__status--error {
                    color: var(--arti24-color-danger, #dc2626);
                }
            </style>

            <div class="arti24-depreciation-calculator">
                ${this.hideTitle ? nothing : html`<h2>${d.depreciationCalculator}</h2>`}

                <div class="arti24-dc__top">
                    <div class="arti24-dc__card arti24-dc__card--input">
                        <div class="arti24-dc__card-header">
                            <span class="arti24-dc__card-icon">${headerIcon('money')}</span>
                            <div class="arti24-dc__card-heading">
                                <span class="arti24-dc__section-label">${d.inputLabel}</span>
                                <h3>${d.fixedAsset}</h3>
                            </div>
                        </div>

                        <div class="arti24-dc__fields">
                          <div class="arti24-dc__field">
                            <label for="arti24-dc-initial">${d.initialValue}</label>
                            <div class="arti24-dc__input-wrap">
                                ${fieldIcon('money')}
                                <input
                                    id="arti24-dc-initial"
                                    type="number"
                                    .value=${String(this.initialValue)}
                                    @input=${(e: Event) =>
                                        (this.initialValue = Number((e.target as HTMLInputElement).value))}
                                />
                                <span class="arti24-dc__input-suffix">zł</span>
                            </div>
                          </div>

                          <div class="arti24-dc__field">
                            <label for="arti24-dc-rate">${d.depreciationRate}</label>
                            <div class="arti24-dc__input-wrap">
                                ${fieldIcon('percent')}
                                <input
                                    id="arti24-dc-rate"
                                    type="number"
                                    .value=${String(this.rate)}
                                    @input=${(e: Event) => (this.rate = Number((e.target as HTMLInputElement).value))}
                                />
                                <span class="arti24-dc__input-suffix">%</span>
                            </div>
                          </div>

                          <div class="arti24-dc__field">
                            <label for="arti24-dc-start">${d.startOfDepreciation}</label>
                            <div class="arti24-dc__input-wrap">
                                ${fieldIcon('calendar')}
                                <input
                                    id="arti24-dc-start"
                                    type="month"
                                    .value=${this.startDepreciation}
                                    @input=${(e: Event) =>
                                        (this.startDepreciation = (e.target as HTMLInputElement).value)}
                                />
                            </div>
                          </div>

                          <div class="arti24-dc__field">
                            <label for="arti24-dc-type">${d.depreciationMethod}</label>
                            <div class="arti24-dc__input-wrap">
                                ${fieldIcon('chart')}
                                <select
                                    id="arti24-dc-type"
                                    .value=${this.depreciationType}
                                    @change=${(e: Event) =>
                                        (this.depreciationType = (e.target as HTMLSelectElement)
                                            .value as DepreciationType)}
                                >
                                    <option value="linear">${d.linear}</option>
                                    <option value="digressive">${d.digressive}</option>
                                </select>
                                <svg class="arti24-dc__chevron" viewBox="0 0 24 24" aria-hidden="true">
                                    <path d="m6 9 6 6 6-6" />
                                </svg>
                            </div>
                          </div>

                          ${
                              this.depreciationType === 'digressive'
                                  ? html`
                                  <div class="arti24-dc__field">
                                      <label for="arti24-dc-factor">${d.degressionCoefficients}</label>
                                      <div class="arti24-dc__input-wrap">
                                          ${fieldIcon('chart')}
                                          <input
                                              id="arti24-dc-factor"
                                              type="number"
                                              step="0.1"
                                              .value=${String(this.factor)}
                                              @input=${(e: Event) =>
                                                  (this.factor = Number((e.target as HTMLInputElement).value))}
                                          />
                                          <span class="arti24-dc__input-suffix">×</span>
                                      </div>
                                  </div>
                              `
                                  : nothing
                          }
                        </div>

                        <div class="arti24-dc__actions">
                            <button type="button" @click=${() => this.addLifeChange()}>
                                <svg viewBox="0 0 24 24" aria-hidden="true">${ICONS.plus}</svg>
                                ${d.addChangeValue}
                            </button>
                            <button
                                type="button"
                                class="arti24-dc__primary"
                                ?disabled=${this.loading}
                                @click=${() => this.recalculate()}
                            >
                                <svg viewBox="0 0 24 24" aria-hidden="true">${ICONS.refresh}</svg>
                                ${d.recalculate}
                            </button>
                        </div>

                        ${
                            this.loading
                                ? html`
                                  <p class="arti24-dc__status">
                                      <svg viewBox="0 0 24 24" aria-hidden="true">${ICONS.spinner}</svg>
                                      ${d.loading}
                                  </p>
                              `
                                : nothing
                        }
                        ${
                            this.errorMessage
                                ? html`<p class="arti24-dc__status arti24-dc__status--error">${this.errorMessage}</p>`
                                : nothing
                        }
                    </div>

                    ${
                        this.lifeChanges.length > 0
                            ? html`
                              <div class="arti24-dc__card">
                                  <div class="arti24-dc__card-header">
                                      <span class="arti24-dc__card-icon">${headerIcon('chart')}</span>
                                      <h3>${d.valueChanges}</h3>
                                  </div>
                                  <div class="arti24-dc__table-wrap">
                                      <table>
                                          <thead>
                                              <tr>
                                                  <th>${d.lp}</th>
                                                  <th>${d.month}</th>
                                                  <th>${d.change}</th>
                                                  <th></th>
                                              </tr>
                                          </thead>
                                          <tbody>
                                              ${this.lifeChanges.map(
                                                  (change, index) => html`
                                                      <tr>
                                                          <th scope="row">${index + 1}</th>
                                                          <td>
                                                              <input
                                                                  type="month"
                                                                  .value=${change.when}
                                                                  @input=${(e: Event) =>
                                                                      this.updateLifeChange(index, {
                                                                          when: (e.target as HTMLInputElement).value,
                                                                      })}
                                                              />
                                                          </td>
                                                          <td>
                                                              <input
                                                                  type="number"
                                                                  .value=${String(change.initial)}
                                                                  @input=${(e: Event) =>
                                                                      this.updateLifeChange(index, {
                                                                          initial: Number(
                                                                              (e.target as HTMLInputElement).value,
                                                                          ),
                                                                      })}
                                                              />
                                                          </td>
                                                          <td>
                                                              <button
                                                                  type="button"
                                                                  class="arti24-dc__remove"
                                                                  aria-label=${d.remove}
                                                                  title=${d.remove}
                                                                  @click=${() => this.removeLifeChange(index)}
                                                              >
                                                                  <svg viewBox="0 0 24 24" aria-hidden="true">${ICONS.trash}</svg>
                                                              </button>
                                                          </td>
                                                      </tr>
                                                  `,
                                              )}
                                          </tbody>
                                      </table>
                                  </div>
                              </div>
                          `
                            : nothing
                    }
                </div>

                ${
                    this.results.length > 0
                        ? html`
                          <div class="arti24-dc__card arti24-dc__card--result">
                              <div class="arti24-dc__card-header">
                                  <span class="arti24-dc__card-icon">${headerIcon('chart')}</span>
                                  <div class="arti24-dc__card-heading">
                                      <span class="arti24-dc__section-label">${d.resultLabel}</span>
                                      <h3>${d.depreciationPlan}</h3>
                                  </div>
                              </div>

                              <div class="arti24-dc__summary">
                                  <div class="arti24-dc__stat">
                                      <span class="arti24-dc__stat-label">${d.summaryInitial}</span>
                                      <span class="arti24-dc__stat-value">${this.formatNumber(this.initialValue)} zł</span>
                                  </div>
                                  <div class="arti24-dc__stat">
                                      <span class="arti24-dc__stat-label">${d.summaryDuration}</span>
                                      <span class="arti24-dc__stat-value">${d.summaryMonths.replace('{count}', String(totalMonths))}</span>
                                  </div>
                                  <div class="arti24-dc__stat arti24-dc__stat--accent">
                                      <span class="arti24-dc__stat-label">${d.summaryTotal}</span>
                                      <span class="arti24-dc__stat-value">${this.formatNumber(totalSum)} zł</span>
                                  </div>
                                  <div class="arti24-dc__stat">
                                      <span class="arti24-dc__stat-label">${d.summaryEnd}</span>
                                      <span class="arti24-dc__stat-value"
                                          >${endWhen ? this.formatMonthYear(endWhen) : '—'}</span
                                      >
                                  </div>
                              </div>

                              <div class="arti24-dc__table-wrap">
                                  <table>
                                      <thead>
                                          <tr>
                                              <th>${d.lp}</th>
                                              <th>${d.year}</th>
                                              <th>${d.month}</th>
                                              <th>${d.amountWriteOf}</th>
                                              <th>${d.totalWriteOf}</th>
                                          </tr>
                                      </thead>
                                      <tbody>
                                          ${this.results.map((position, index) => {
                                              const isYearEnd =
                                                  position.when.month === 12 || index === this.results.length - 1;

                                              return html`
                                                  <tr
                                                      class=${
                                                          index % 2 === 0
                                                              ? 'arti24-dc__month-row arti24-dc__month-row--odd'
                                                              : 'arti24-dc__month-row arti24-dc__month-row--even'
                                                      }
                                                  >
                                                      <th scope="row">${index + 1}</th>
                                                      <td>${position.when.year}</td>
                                                      <td>${position.when.month}</td>
                                                      <td>${this.formatNumber(position.calculatedDepreciation)}</td>
                                                      <td>${this.formatNumber(position.sum)}</td>
                                                  </tr>
                                                  ${
                                                      isYearEnd
                                                          ? html`
                                                            <tr class="arti24-dc__year-total">
                                                                <td colspan="5">
                                                                    ${d.totalInTheYear.replace('{year}', String(position.when.year)).replace('{total}', this.formatNumber(position.sumThisYear))}
                                                                </td>
                                                            </tr>
                                                        `
                                                          : nothing
                                                  }
                                              `;
                                          })}
                                      </tbody>
                                  </table>
                              </div>
                          </div>
                      `
                        : nothing
                }
            </div>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'arti24-depreciation-calculator': Arti24DepreciationCalculator;
    }
}
